import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Alert, Button, DatePicker, Spin} from 'antd';
import moment from 'moment';
import {displayAmount} from "../utils/money";
import axios from "axios";
import {authorizationHeaders} from "../utils/api";
import "./ReturnsReportPage.css";
import SelectCategoryTree from "../Category/SelectCategoryTree";
import {bindActionCreators} from "redux";
import {getCategoryTree} from "../Category/actions";
import {getCategoryAndChildrenIds} from "../Category/tree";
import {defaultComparer} from "../utils/compare";

const maxResults = 10000;

const styles = {
  floatRight: {
    float: 'right',
  },
};

export class ReturnsReportPage extends Component {
  static propTypes = {
    /* state */
    dispensaryId: PropTypes.string.isRequired,
    categoryTree: PropTypes.object.isRequired,
    /* dispatch */
    getCategoryTree: PropTypes.func.isRequired,
  };

  state = {
    dateTimeFrom: moment().startOf('day'),
    dateTimeTo: moment().endOf('day'),
    loading: false,
    error: '',
    categoryId: '',
    reasons: [],
    totalProductRefunds: 0,
    totalFeesRefund: 0,
    totalTaxesRefund: 0,
    totalRefunds: 0,
    dataCount: 0,
  };

  componentDidMount() {
    this._loadCategories();
    this._loadData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.dispensaryId !== prevProps.dispensaryId) {
      this._loadCategories();
      this._loadData();
    }
    if (this.state.categoryId !== prevState.categoryId) {
      this._loadData();
    }
  }

  _loadCategories() {
    const {dispensaryId, getCategoryTree} = this.props;
    getCategoryTree({dispensaryId});
  }

  _loadData() {
    const filter = {
      where: {
        dispensaryId: {eq: this.props.dispensaryId},
        datetime: {between: [this.state.dateTimeFrom, this.state.dateTimeTo]},
      },
      limit: maxResults,
    };
    if (this.state.categoryId) {
      const categoryIds = getCategoryAndChildrenIds(this.props.categoryTree, this.state.categoryId);
      filter.where.categoryId = {inq: categoryIds};
    }
    this.setState({loading: true, error: ''});
    axios.get(
      `${process.env.PUBLIC_URL}/api/v1/r/returnsReport?filter=${encodeURIComponent(JSON.stringify(filter))}&_=${Date.now()}`,
      {headers: authorizationHeaders()}
    ).then(res => this._processData(res.data), err => this.setState({error: err.message}))
      .finally(() => this.setState({loading: false}));
  }

  _processData(data) {
    const {reasonsById, totalProductRefunds, totalFeesRefund, totalTaxesRefund, totalRefunds} = data.reduce(
      (
        {reasonsById, totalProductRefunds, totalFeesRefund, totalTaxesRefund, totalRefunds},
        {
          orderItemId, orderId, categoryId, categoryName, customerName, productDescription, productRefund, feesRefund,
          taxesRefund, totalRefund, datetime, returnReasonId, returnReasonName, note,
        },
      ) => {
        const reason = reasonsById[returnReasonId] || (reasonsById[returnReasonId] = {returnReasonId, returnReasonName, categoriesById: {}, totalProductRefunds: 0, totalFeesRefund: 0, totalTaxesRefund: 0, totalRefunds: 0});
        const cat = reason.categoriesById[categoryId] || (reason.categoriesById[categoryId] = {categoryId, categoryName, returns: []});
        cat.returns.push({
          orderItemId, customerName, productDescription, productRefund, feesRefund, taxesRefund, totalRefund, note,
          date: moment(datetime).format('YYYY-MM-DD'),
          orderNumber: orderId.substr(0, 8),
        });
        reason.totalProductRefunds += productRefund;
        totalProductRefunds += productRefund;
        reason.totalFeesRefund += feesRefund;
        totalFeesRefund += feesRefund;
        reason.totalTaxesRefund += taxesRefund;
        totalTaxesRefund += taxesRefund;
        reason.totalRefunds += totalRefund;
        totalRefunds += totalRefund;
        return {reasonsById, totalProductRefunds, totalFeesRefund, totalTaxesRefund, totalRefunds};
      },
      {reasonsById: {}, totalProductRefunds: 0, totalFeesRefund: 0, totalTaxesRefund: 0, totalRefunds: 0}
    );
    const reasons = Object.values(reasonsById).sort(({returnReasonName: a}, {returnReasonName: b}) => defaultComparer(a, b))
      .map(item => ({...item, categories: Object.values(item.categoriesById).sort(({categoryName: a}, {categoryName: b}) => defaultComparer(a, b))
        .map(item => ({...item, returns: item.returns.sort(({productDescription: a}, {productDescription: b}) =>
          defaultComparer(a.substr(a.indexOf('x') + 2), b.substr(a.indexOf('x') + 2))
        )}))}));
    this.setState({reasons, totalProductRefunds, totalFeesRefund, totalTaxesRefund, totalRefunds, dataCount: data.length});
  }

  _onCategoryChange = (categoryId) => {
    this.setState({categoryId});
  };

  _onRangePickerChange = (values) => {
    const [dateTimeFrom, dateTimeTo] = values;
    if (!dateTimeFrom || !dateTimeTo) return;
    this.setState({dateTimeFrom, dateTimeTo});
  };

  _print = () => window.print();

  render() {
    const {
      dateTimeFrom, dateTimeTo, loading, categoryId, reasons, totalProductRefunds, totalFeesRefund, totalTaxesRefund,
      totalRefunds,
    } = this.state;
    return (
      <div>
        <div className="transaction-history-report-form">
          <Button icon="printer" style={styles.floatRight} onClick={this._print}>Print</Button>
          <DatePicker.RangePicker
            showTime={{defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('11:59:59', 'HH:mm:ss')]}}
            format="YYYY-MM-DD HH:mm:ss"
            value={[dateTimeFrom, dateTimeTo]}
            onChange={this._onRangePickerChange}
            onOk={() => this._loadData()}
          />
          <SelectCategoryTree
            tree={this.props.categoryTree}
            showAll
            value={categoryId}
            onChange={this._onCategoryChange}
            style={{minWidth: '300px'}}
          />
          {this._renderAlert()}
        </div>
        <Spin spinning={loading}>
          <h1>Returns Report</h1>
          <p>
            <strong>Period:</strong> {dateTimeFrom.format('MMMM Do YYYY, h:mm:ss a')} to {dateTimeTo.format('MMMM Do YYYY, h:mm:ss a')}
          </p>
          <table id="returnsReportTable">
            <thead>
              <tr>
                <th>Discount Reason</th>
                <th>Category</th>
                <th>Product Description</th>
                <th>Date</th>
                <th>Product Refund</th>
                <th>Fees Refund</th>
                <th>Taxes Refund</th>
                <th>Total Refund</th>
                <th>Order #</th>
                <th>Customer Name</th>
                <th>Return Note</th>
              </tr>
            </thead>
            <tbody>
              {reasons.map(({returnReasonId, returnReasonName, categories, totalProductRefunds, totalFeesRefund, totalTaxesRefund, totalRefunds}) =>
                <React.Fragment key={returnReasonId}>
                  {categories.map(({categoryId, categoryName, returns}) => returns.map((ret) =>
                    <tr key={categoryId}>
                      <td>{returnReasonName}</td>
                      <td>{categoryName}</td>
                      <td>{ret.productDescription}</td>
                      <td>{ret.date}</td>
                      <td>{displayAmount(ret.productRefund)}</td>
                      <td>{displayAmount(ret.feesRefund)}</td>
                      <td>{displayAmount(ret.taxesRefund)}</td>
                      <td>{displayAmount(ret.totalRefund)}</td>
                      <td>{ret.orderNumber}</td>
                      <td>{ret.customerName}</td>
                      <td>{ret.note}</td>
                    </tr>
                  ))}
                  <tr key={returnReasonId}>
                    <th colSpan={4}>Sub-Total</th>
                    <th>{displayAmount(totalProductRefunds)}</th>
                    <th>{displayAmount(totalFeesRefund)}</th>
                    <th>{displayAmount(totalTaxesRefund)}</th>
                    <th>{displayAmount(totalRefunds)}</th>
                    <th colSpan={3}>&nbsp;</th>
                  </tr>
                </React.Fragment>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={4}>Total</th>
                <th>{displayAmount(totalProductRefunds)}</th>
                <th>{displayAmount(totalFeesRefund)}</th>
                <th>{displayAmount(totalTaxesRefund)}</th>
                <th>{displayAmount(totalRefunds)}</th>
                <th colSpan={3}>&nbsp;</th>
              </tr>
            </tfoot>
          </table>
        </Spin>
      </div>
    );
  }

  _renderAlert() {
    const {error, dataCount} = this.state;
    let type, message, description;
    if (error) {
      type = 'error';
      message = 'Error';
      description = error;
    } else if (dataCount === 0) {
      type = 'info';
      message = "No data found";
      description = "Choose a different date range";
    } else if (dataCount === maxResults) {
      type = 'warning';
      message = "Too many data";
      description = "Choose a smaller date range";
    } else {
      return null;
    }
    return (<Alert type={type} message={message} description={description}/>);
  }
}

const mapStateToProps = (state) => ({
  dispensaryId: state.dispensary.selectedDispensaryId,
  categoryTree: state.category.categoryTree,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({getCategoryTree}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ReturnsReportPage);



// WEBPACK FOOTER //
// ./src/Reports/ReturnsReportPage.js